import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import ImageTag from "../components/reuseable/imageTag";
import {
  RequestPasswordResetOtpAction,
  VerifyPasswordOtpAction,
  clearPendingOtpForPasswordReset,
} from "../Store/Actions/AuthActions";
import { PasswordOtpActionConst } from "../Store/Constants/AuthConstants";

export default function OTPPasswordReset({ length = 6, autoFocus = true }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    pendingEmailForPasswordOtp,
    passwordOtpRequestLoading,
    passwordOtpRequestError,
    passwordOtpRequestSuccess,
    passwordOtpVerifyLoading,
    passwordOtpVerifySuccess,
    passwordOtpVerifyError,
  } = useSelector((state) => state.auth);

  const [values, setValues] = useState(Array.from({ length }, () => ""));
  const inputsRef = useRef([]);
  const hasHandledSuccess = useRef(false); // Track if we've already handled success

  useEffect(() => {
    if (!pendingEmailForPasswordOtp) {
      navigate("/forget-password");
    }
  }, [pendingEmailForPasswordOtp, navigate]);

  // Clear password OTP request states on mount to prevent stale toasts
  useEffect(() => {
    dispatch(clearPendingOtpForPasswordReset());
    // Clear success/error states to prevent stale toasts
    dispatch({
      type: PasswordOtpActionConst.REQUEST_CODE_FAIL,
      result: null,
    });
    // Don't clear on unmount - we need the OTP state when navigating to password change screen
  }, [dispatch]);

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!passwordOtpRequestError) return;
    const message = Array.isArray(passwordOtpRequestError.message)
      ? passwordOtpRequestError.message.join(", ")
      : passwordOtpRequestError.message || "Unable to send OTP. Try again.";
    toast.error(message);
  }, [passwordOtpRequestError]);

  useEffect(() => {
    if (!passwordOtpVerifyError) return;
    const message = Array.isArray(passwordOtpVerifyError.message)
      ? passwordOtpVerifyError.message.join(", ")
      : passwordOtpVerifyError.message || "Invalid or expired OTP.";
    // setOtpError(message);
    toast.error(message);
  }, [passwordOtpVerifyError]);

  useEffect(() => {
    // Only handle success if we're actually on the OTP password reset page and haven't handled it yet
    if (!passwordOtpVerifySuccess || location.pathname !== "/otp-password-reset" || hasHandledSuccess.current) return;
    
    // Mark as handled immediately to prevent duplicate calls
    hasHandledSuccess.current = true;
    
    const message =
      passwordOtpVerifySuccess?.message ||
      passwordOtpVerifySuccess?.data?.message ||
      "OTP verified successfully.";
    toast.success(message);
    // setOtpError("");
    setValues(Array.from({ length }, () => ""));
    // Navigate to password change screen
    navigate("/change-password");
  }, [passwordOtpVerifySuccess, location.pathname, length, navigate, dispatch]);

  // Reset the ref when passwordOtpVerifySuccess is cleared (allows handling success again if needed)
  useEffect(() => {
    if (!passwordOtpVerifySuccess) {
      hasHandledSuccess.current = false;
    }
  }, [passwordOtpVerifySuccess]);

  useEffect(() => {
    // Only handle success if we're actually on the OTP password reset page and haven't handled it yet
    // This prevents the effect from firing when component remounts or state persists
    if (!passwordOtpRequestSuccess || location.pathname !== "/otp-password-reset" || hasHandledSuccess.current) return;
    
    // Mark as handled immediately to prevent duplicate calls
    hasHandledSuccess.current = true;
    
    const message =
      passwordOtpRequestSuccess?.message ||
      passwordOtpRequestSuccess?.data?.message ||
      "If the email exists, a password reset code has been sent.";
    toast.success(message);
    // Clear success state to prevent re-triggering
    dispatch({
      type: PasswordOtpActionConst.REQUEST_CODE_FAIL,
      result: null,
    });
  }, [passwordOtpRequestSuccess, location.pathname, dispatch]);

  // Reset the ref when passwordOtpRequestSuccess is cleared (allows handling success again if needed)
  useEffect(() => {
    if (!passwordOtpRequestSuccess) {
      hasHandledSuccess.current = false;
    }
  }, [passwordOtpRequestSuccess]);

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (e, idx) => {
    const raw = e.target.value;
    const char = raw.replace(/[^0-9]/g, "").slice(0, 1);
    if (!char) return;

    const next = [...values];
    next[idx] = char;
    setValues(next);
    // setOtpError("");

    if (idx < length - 1) {
      inputsRef.current[idx + 1].focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...values];
      if (next[idx]) {
        next[idx] = "";
        setValues(next);
        inputsRef.current[idx].focus();
        return;
      }
      if (idx > 0) {
        next[idx - 1] = "";
        setValues(next);
        inputsRef.current[idx - 1].focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1].focus();
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      inputsRef.current[idx + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData("text");
    const digits = pasted
      .replace(/[^0-9]/g, "")
      .slice(0, length)
      .split("");
    if (digits.length === 0) return;
    const next = Array.from({ length }, (_, i) => digits[i] || "");
    setValues(next);
    const firstEmpty = next.findIndex((v) => !v);
    const focusIdx = firstEmpty === -1 ? length - 1 : firstEmpty;
    inputsRef.current[focusIdx].focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const otp = values.join("");
    if (otp.length !== length) {
      // setOtpError("Please enter the full OTP.");
      return;
    }
    if (!pendingEmailForPasswordOtp) {
      // setOtpError("Email not found. Please request a new OTP.");
      navigate("/forget-password");
      return;
    }
    // setOtpError("");
    // Verify the OTP with the backend
    dispatch(
      VerifyPasswordOtpAction({
        email: pendingEmailForPasswordOtp,
        otp: otp,
      })
    );
  };

  const handleResendCode = () => {
    if (!pendingEmailForPasswordOtp || passwordOtpRequestLoading) return;
    dispatch(
      RequestPasswordResetOtpAction({
        email: pendingEmailForPasswordOtp,
        onSuccess: () => toast.success("OTP resent successfully."),
        onError: (err) => {
          const message = Array.isArray(err?.message)
            ? err.message.join(", ")
            : err?.message || "Unable to resend OTP. Try again.";
          toast.error(message);
        },
      })
    );
  };

  return (
    <div className="lg:h-screen flex items-center">
      <div className="flex w-full lg:h-screen rounded-xl">
        <div className="w-[60%] h-full hidden lg:flex lg:flex-col justify-center items-center bg-gradient-to-b from-[#3FC4E3] to-[#006077]">
          <ImageTag
            path="/assets/images/auth/thumbnail.png"
            classes="size-[460px]"
            altText="password reset"
          />
          <p className="font-popinsBold text-white text-4xl mt-8 text-center w-[70%]">
            Enter the one-time code to continue resetting your password.
          </p>
        </div>
        <div className=" w-full lg:w-[40%] h-screen lg:h-full my-auto flex flex-col items-center justify-center bg-bg-auth bg-no-repeat bg-center bg-cover">
          <div className="w-[90%] lg:w-[75%] mx-auto flex flex-col items-center justify-center">
            <div className="w-full pt-8 h-full flex flex-col justify-center text-center">
              <h1 className="font-popinsBold  text-2xl 2xl:text-4xl">
                Enter OTP
              </h1>
              <p className="text-textColor mb-4 w-[80%] mx-auto mt-2">
                We sent a {length}-digit code to{" "}
                <span className="font-popinsSemiBold">
                  {pendingEmailForPasswordOtp || "your email"}
                </span>{" "}
                for password reset.
              </p>
              <form onSubmit={handleSubmit} className="w-full max-w-md mx-auto">
                <div
                  className="flex gap-2 justify-center mb-4"
                  onPaste={handlePaste}
                >
                  {Array.from({ length }).map((_, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputsRef.current[idx] = el)}
                      value={values[idx]}
                      onChange={(e) => handleChange(e, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      className="w-12 h-12 sm:w-14 sm:h-14 text-center font-popinsMedium rounded-xl border-2 border-border bg-white px-4 py-2 !text-black/50  outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
                      aria-label={`Digit ${idx + 1}`}
                    />
                  ))}
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    disabled={passwordOtpVerifyLoading}
                    className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-lg mt-8 rounded-md shadow-lg
           transform transition duration-300 ease-in-out
           hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
           active:scale-95 active:shadow-inner focus:outline-none w-full
           disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <span className="flex items-center justify-center">
                      {passwordOtpVerifyLoading ? (
                        <>
                          <svg
                            aria-hidden="true"
                            className="w-6 h-6 text-gray-200 animate-spin dark:text-white fill-indigo-600"
                            viewBox="0 0 100 101"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                              fill="currentColor"
                            />
                            <path
                              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                              fill="currentFill"
                            />
                          </svg>
                          <span className="ml-2">Verifying...</span>
                        </>
                      ) : (
                        "Continue"
                      )}
                    </span>
                    <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
                  </button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={
                      passwordOtpRequestLoading || !pendingEmailForPasswordOtp
                    }
                    className="text-sm text-gray-500 font-popinsMedium underline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {passwordOtpRequestLoading ? "Resending..." : "Resend code"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

